import React, { useEffect, useState, useMemo } from "react";
import {
  adminActions,
  adminSelectors,
  authSelectors,
  useDispatch,
  useSelector,
} from "../../state";
import { useParams } from "react-router";
import { serviceRequestsActions } from "../../state/serviceRequests/actions";
import { serviceRequestsSelectors } from "../../state/serviceRequests/selectors";
import {
  Typography,
  Grid,
  Chip,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Card,
  CardContent,
  Avatar,
  Link,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Menu,
} from "@mui/material";
import {
  formatPolicyType,
  formatRequestType,
  getServiceRequestColor,
  getServiceRequestStatus,
} from "../../utils";
import {
  EmailOutlined,
  PhoneOutlined,
  DescriptionOutlined,
  AttachFileOutlined,
  BusinessOutlined,
  DateRangeOutlined,
  PolicyOutlined,
  ReceiptOutlined,
  MailOutline,
  AssignmentInd,
  EditOutlined,
} from "@mui/icons-material";
import { format } from "date-fns";
import ServiceEmailModal, {
  AddNoteModal,
} from "../../components/modals/ServiceEmailModal";
import { StatusChip } from "../../components/components";
const statuses = ["new", "in_progress", "pending_response", "closed"];

export default function ServiceRequestPage() {
  // Get uuid from params
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();
  const serviceRequest = useSelector(
    serviceRequestsSelectors.serviceRequestData,
  );
  const role = useSelector(authSelectors.activeRole);
  const allDirectors = useSelector(adminSelectors.directors);
  const directors = allDirectors.filter((d) => d.is_service_agent);
  const [selectedDirector, setSelectedDirector] = useState<any>(
    serviceRequest?.user_id || "",
  );
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const open = Boolean(anchorEl);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  useEffect(() => {
    dispatch(serviceRequestsActions.getServiceRequestByUUID(uuid));
  }, [dispatch, uuid]);

  useEffect(() => {
    if (role === "agency_admin") return;
    dispatch(adminActions.getDirectors());
  }, [dispatch, role]);

  // Set initial selected director when service request loads or changes
  useEffect(() => {
    if (serviceRequest?.user_id) {
      setSelectedDirector(serviceRequest.user_id.toString());
    } else {
      setSelectedDirector("");
    }
  }, [serviceRequest?.user_id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMM d, yyyy h:mm a");
  };

  // Get sender name helper
  const getSenderName = (communication) => {
    if (communication.direction === "outbound") {
      // Message from agent to client
      return communication.user
        ? `${communication.user.first_name} ${communication.user.last_name}`
        : "Agent";
    } else {
      // Message from client to agent
      return `${serviceRequest.first_name} ${serviceRequest.last_name}`;
    }
  };

  // Sort communications by date
  const sortedCommunications = useMemo(() => {
    if (!serviceRequest.communications?.length) return [];
    return [...serviceRequest.communications].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [serviceRequest.communications]);

  const handleStatusChange = (event: SelectChangeEvent) => {
    const newStatus = event.target.value;
    if (uuid && newStatus !== serviceRequest?.status) {
      // this is only for status update
      dispatch(
        serviceRequestsActions.updateServiceRequest(uuid, {
          status: newStatus,
        }),
      );
    }
  };
  const handleDirectorChange = (event: SelectChangeEvent) => {
    setSelectedDirector(event.target.value);
  };

  const handleAssignDirector = () => {
    if (selectedDirector && uuid) {
      dispatch(
        serviceRequestsActions.assignServiceRequestToDirector(
          uuid,
          selectedDirector,
        ),
      );
      setSelectedDirector("");
    }
  };
  const handleOpenEmailModal = () => {
    setEmailModalOpen(true);
  };
  const handleCloseEmailModal = () => {
    setEmailModalOpen(false);
  };
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleTransferClick = () => {
    setAnchorEl(null);
    setTransferModalOpen(true);
  };
  const handleCloseTransferModal = () => {
    setTransferModalOpen(false);
  };

  return (
    <Box sx={{ maxWidth: "1200px", p: 2 }}>
      <Box
        sx={{
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1, sm: 2 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 600 }}
            >
              Service Request #{serviceRequest?.id}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Chip
                label={formatRequestType(serviceRequest?.request_type)}
                size="small"
                sx={{ fontWeight: 500 }}
              />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <StatusChip status={serviceRequest?.status} />
                {role !== "agency_admin" && (
                  <IconButton
                    onClick={(e) => {
                      setStatusAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      ml: 0.5,
                      p: 0.5,
                    }}
                  >
                    <EditOutlined sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
                <Menu
                  anchorEl={statusAnchorEl}
                  open={Boolean(statusAnchorEl)}
                  onClose={() => setStatusAnchorEl(null)}
                >
                  {statuses.map((status) => (
                    <MenuItem
                      onClick={() => {
                        handleStatusChange({
                          target: { value: status },
                        } as SelectChangeEvent);
                        setStatusAnchorEl(null);
                      }}
                      selected={serviceRequest?.status === status}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: getServiceRequestColor(status),
                          mr: 1,
                        }}
                      />
                      {getServiceRequestStatus(status)}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>
          </Box>

          {/* Right side: Action buttons - full width on mobile */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", sm: "flex-end" },
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 1, sm: 0 },
              flexWrap: "wrap",
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Email client button */}
            <Button
              variant="outlined"
              startIcon={<MailOutline />}
              onClick={() => setNoteModalOpen(true)}
              size="small"
            >
              Add Note
            </Button>
            {role !== "agency_admin" && (
              <Button
                variant="outlined"
                startIcon={<MailOutline />}
                onClick={handleOpenEmailModal}
                size="small"
              >
                Email Client
              </Button>
            )}

            {/* Director avatar/assignment */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {serviceRequest?.user && (
                <>
                  <IconButton onClick={handleAvatarClick}>
                    <Avatar sx={{ bgcolor: "#4f5ce1" }}>
                      {`${serviceRequest.user?.first_name?.[0]}${serviceRequest.user?.last_name?.[0]}`}
                    </Avatar>
                  </IconButton>
                  <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    <Paper sx={{ p: 2, maxWidth: 320 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Assigned to:
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar sx={{ bgcolor: "#4f5ce1", mr: 2 }}>
                          {`${serviceRequest.user?.first_name?.[0]}${serviceRequest.user?.last_name?.[0]}`}
                        </Avatar>
                        <Box>
                          <Typography variant="body1">
                            {serviceRequest.user.first_name}{" "}
                            {serviceRequest.user.last_name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {serviceRequest.user.email}
                          </Typography>
                        </Box>
                      </Box>
                      {role !== "agency_admin" && (
                        <Button
                          startIcon={<AssignmentInd />}
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={handleTransferClick}
                          size="small"
                        >
                          TRANSFER OWNERSHIP
                        </Button>
                      )}
                    </Paper>
                  </Popover>
                </>
              )}
              {/* Assign director */}
              {role !== "agency_admin" && !serviceRequest?.user && (
                <Button
                  variant="outlined"
                  startIcon={<AssignmentInd />}
                  onClick={handleTransferClick}
                  size="small"
                >
                  Assign
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={1}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{ mb: 1.5, border: "1px solid #e0e0e0", minHeight: 325 }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "#4f5ce1", mr: 2 }}>
                  {`${serviceRequest?.first_name?.[0]}${serviceRequest?.last_name?.[0]}`}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {serviceRequest?.first_name} {serviceRequest?.last_name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Submitted {formatDate(serviceRequest?.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <List
                disablePadding
                sx={{
                  "& .MuiListItem-root": {
                    py: 0,
                  },
                }}
              >
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EmailOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Link href={`mailto:${serviceRequest?.email}`}>
                        {serviceRequest?.email}
                      </Link>
                    }
                    secondary="Email"
                  />
                </ListItem>

                {serviceRequest?.phone && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link href={`tel:${serviceRequest?.phone}`}>
                          {serviceRequest?.phone}
                        </Link>
                      }
                      secondary="Phone"
                    />
                  </ListItem>
                )}

                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BusinessOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={serviceRequest?.agency?.name}
                    secondary="Agency"
                  />
                </ListItem>

                {serviceRequest?.preferred_contact_method && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`Preferred contact: ${
                        serviceRequest?.preferred_contact_method === "email"
                          ? "Email"
                          : "Phone"
                      }`}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "textSecondary",
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Policy Information */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{ mb: 1.5, border: "1px solid #e0e0e0", minHeight: 325 }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Policy Information
              </Typography>

              <List
                disablePadding
                sx={{
                  "& .MuiListItem-root": {
                    py: 0,
                  },
                }}
              >
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PolicyOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={formatPolicyType(serviceRequest?.policy_type)}
                    secondary="Policy Type"
                  />
                </ListItem>

                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ReceiptOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={serviceRequest?.policy_carrier || "N/A"}
                    secondary="Policy Carrier"
                  />
                </ListItem>

                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DescriptionOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={serviceRequest?.policy_number || "N/A"}
                    secondary="Policy Number"
                  />
                </ListItem>

                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DateRangeOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      serviceRequest?.due_date
                        ? format(
                            new Date(serviceRequest.due_date),
                            "MMM d, yyyy",
                          )
                        : "N/A"
                    }
                    secondary="Due Date"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        {/* Request Details */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1.5, border: "1px solid #e0e0e0", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                Request Description
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {serviceRequest?.description || "No description provided"}
            </Typography>
          </Box>
        </Grid>
        {/* Notes */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1.5, border: "1px solid #e0e0e0", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notes
              </Typography>
            </Box>
            {serviceRequest?.notes && serviceRequest.notes.length > 0 ? (
              <Box>
                {serviceRequest.notes.map((note, index) => (
                  <Box
                    key={note.id || index}
                    sx={{
                      my: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}></Box>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                      >
                        {formatDate(note.created_at)} -
                      </Typography>{" "}
                      {note.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No notes yet.
              </Typography>
            )}
          </Box>
        </Grid>
        {/* Communications */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8f8f8" }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Communication History
            </Typography>
            {sortedCommunications.length > 0 ? (
              <Box sx={{ maxHeight: "500px", overflowY: "scroll", px: 1 }}>
                {sortedCommunications.map((communication) => {
                  const isOutbound = communication.direction === "outbound";
                  return (
                    <Box
                      key={communication.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: isOutbound ? "flex-end" : "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ml: isOutbound ? 0 : 2,
                          mr: isOutbound ? 2 : 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: "medium" }}
                        >
                          {getSenderName(communication)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ ml: 1 }}
                        >
                          {formatDate(communication.created_at)}
                        </Typography>
                      </Box>

                      <Paper
                        elevation={1}
                        sx={{
                          p: 1,
                          maxWidth: "80%",
                          bgcolor: isOutbound ? "primary.light" : "#fff",
                          color: isOutbound
                            ? "primary.contrastText"
                            : "text.primary",
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {communication.message}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic", textAlign: "center" }}
              >
                No communications yet.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Files */}
        {serviceRequest?.files && serviceRequest.files.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ mb: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Attached Files
                </Typography>

                <Stack spacing={1}>
                  {serviceRequest.files.map((file) => (
                    <Box
                      key={file.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                      }}
                    >
                      <AttachFileOutlined sx={{ mr: 1, color: "#666" }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" noWrap>
                          {file.filename}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ ml: 2, minWidth: 100 }}
                        onClick={() =>
                          dispatch(adminActions.download(file.file_path))
                        }
                      >
                        Download
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      <ServiceEmailModal
        open={emailModalOpen}
        onClose={handleCloseEmailModal}
        serviceRequest={serviceRequest}
        uuid={uuid}
      />
      <AddNoteModal
        open={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        uuid={uuid}
      />

      {/* Transfer Ownership Modal */}
      <Dialog open={transferModalOpen} onClose={handleCloseTransferModal}>
        <DialogTitle>Transfer Ownership</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 300, pt: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select a new director to transfer this service request to:
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Select
                value={selectedDirector}
                onChange={handleDirectorChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Director
                </MenuItem>
                {directors?.map((director) => (
                  <MenuItem key={director.user_id} value={director.user_id}>
                    {director.first_name} {director.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferModal}>Cancel</Button>
          <Button
            onClick={() => {
              handleAssignDirector();
              handleCloseTransferModal();
            }}
            variant="contained"
            color="primary"
            disabled={!selectedDirector}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
